import {
    Component,
    OnInit,
    OnDestroy,
    EventEmitter,
    Output,
    Input,
  } from "@angular/core";
  import { Observable, Subject } from "rxjs";
  import { takeUntil, debounceTime, distinctUntilChanged } from "rxjs/operators";
  import {
    FormControl,
    FormGroup,
    Validators,
    FormBuilder,
  } from "@angular/forms";
  
  import {
    HelperService,
    PageService,
    UserService,
    UiService,
  } from "@services/index";
  
  import { CountryService, LanguageService } from "@services/index";
  import { IState, LanguageState } from "@models/index";
  
  @Component({
    selector: 'app-profile-form',
    templateUrl: './profile-form.component.html',
    styleUrls: ['./profile-form.component.scss']
  })
  export class ProfileFormComponent implements OnInit {
    @Input() firstLogin: boolean = false;
    public profileDetailsForm: FormGroup;
    profile: any = {};
    profile$: Observable<any>;
  
    affiliates$: Observable<IState>;
    languages$: Observable<LanguageState>;
    country$: Observable<any>;
    user$: Observable<any>;
  
    languages: any = [];
    languagesSearch = new FormControl();
    selectedLanguage: any;
  
    countries: any = [];
    countriesSearch = new FormControl();
    selectedCountry: any;
    errorObj: any = {};
    private unsubscribe$ = new Subject<void>();
  error: any = {
      errorMessage: "",
      email: "",
      password: "",
    };
    constructor(
      private countryService: CountryService,
      private languageService: LanguageService,
      private formBuilder: FormBuilder,
      private pageService: PageService,
      private userService: UserService,
      private uiService: UiService
    ) { 
      this.profileDetailsForm = this.formBuilder.group({
        first_name: new FormControl(null, [Validators.required]),
        last_name: new FormControl(null, [Validators.required]),
        phone: new FormControl(null),
        fax: new FormControl(null),
        site_url: new FormControl(null),
        company_name: new FormControl(null),
        language_id: new FormControl(null),
        country_id: new FormControl(null),
        state: new FormControl(null),
        city: new FormControl(null),
        street: new FormControl(null),
        zip_code: new FormControl(null),
        note: new FormControl(null),
        file_id: new FormControl(null),
        callback_url: new FormControl(null),
        current_pass: new FormControl(null),
        password: new FormControl(null, [
          // Make it optional but validate if filled
          (control) => {
            if (!control.value) return null; // Skip validation if empty
            
            // Check length
            if (control.value.length < 8 || control.value.length > 20) {
              return { invalidLength: true };
            }
            
            // Check for at least one uppercase and one lowercase letter
            if (!/(?=.*[A-Z])(?=.*[a-z])/.test(control.value)) {
              return { invalidPattern: true };
            }
            
            return null;
          }
        ]),
        repeat_pass: new FormControl(null),
      }, {
        validators: [
          this.mustMatch('password', 'repeat_pass'),
          this.passwordFieldsValidator()
        ]
      });
  
      this.pageService.changePageInfo({ breadcrum: ["Profile Details"] });
      this.profile$ = this.userService.getProfileSelector();
  
      this.languages$ = this.languageService.getLanguagesSelector();
      this.languageService.fetchLanguages({});
  
      this.country$ = this.countryService.getCountrySelector();
      this.countryService.fetchCountries({});

      this.user$ = this.userService.getUserSelector();
    }
  
    ngOnInit() {
        this.profile$.pipe(takeUntil(this.unsubscribe$)).subscribe((profile) => {
        if (HelperService.isObjectEmpty(profile)) return;
        this.profileDetailsForm.patchValue({ file_id: profile.file_id });
        this.profile = profile;
        this.fillValues(this.profile);
      });
  
      this.user$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
        if (user.error && typeof user.error === 'string') {
          this.profileDetailsForm.get('current_pass').setErrors({
            incorrectPassword: true
          });
        }
      });

      // language search
      this.languagesSearch.valueChanges
        .pipe(
          takeUntil(this.unsubscribe$),
          debounceTime(500),
          distinctUntilChanged()
        )
        .subscribe((keyword) => this.languageService.fetchLanguages({ keyword }));
  
      // get language list
      this.getLanguages();
  
      // countries search
      this.countriesSearch.valueChanges
        .pipe(
          takeUntil(this.unsubscribe$),
          debounceTime(500),
          distinctUntilChanged()
        )
        .subscribe((keyword) => this.countryService.fetchCountries({ keyword }));
  
      // get countries list
      this.getCountries();
    }
  
    fillValues(options: any): void {
      HelperService.fillFormValues(options.profile, this.profileDetailsForm);
      this.selectedLanguage = options.profile.language
        ? options.profile.language
        : null;
      this.selectedCountry = options.profile.country
        ? options.profile.country
        : null;
    }
  
    getLanguages(): void {
      this.languages$.pipe(takeUntil(this.unsubscribe$)).subscribe((state) => {
        this.languages = state.data;
      });
    }
  
    getSearchValue(options: any, name: string): void {
      // search select value
      this[name].patchValue(options.term);
    }
  
    onSelectedValue(options: any, name: string): void {
      this.profileDetailsForm.patchValue({ [name]: options ? options.id : null });
    }
  
    getCountries(): void {
      // listen country
      this.country$.pipe(takeUntil(this.unsubscribe$)).subscribe((state) => {
        this.countries = state.data;
      });
    }
  
    uploadImage(files): void {
      const imageFile = HelperService.createFormData(files);
  
      this.uiService
        .uploadImage(imageFile, "user")
        .then((res) => {
          this.profile.image_url = res["url"];
          this.profileDetailsForm.patchValue({ file_id: res["id"] });
        })
        .catch((err) => {
          this.pageService.onNotificationToggle({
            show: true,
            error: true,
            message: "Image Upload Faild!",
          });
        });
    }
  
    deleteImage(): void {
      this.profileDetailsForm.patchValue({ file_id: null });
    }
  
    onChangeFormValue(options): void {
      switch (options.name) {
        case "save":
          this.errorObj = HelperService.checkValidation(this.profileDetailsForm);
          console.log('errorObj',this.errorObj);
          console.log('profileDetailsForm',this.profileDetailsForm);
          if (this.profileDetailsForm.valid) {
            this.userService.updateUser({
              id: this.profile.userId,
              email: this.profile.email,
              ...this.profileDetailsForm.value,
            })
          }
          break;
  
        case "discard":
          this.fillValues(this.profile);
          break;
  
        case "file_id":
          if (options.value.length) {
            this.uploadImage(options.value);
          }
          break;
  
        default:
          this.profileDetailsForm.patchValue({ [options.name]: options.value });
          break;
      }
    }
    mustMatch(controlName: string, matchingControlName: string) {
      return (formGroup: FormGroup) => {
        const control = formGroup.controls[controlName];
        const matchingControl = formGroup.controls[matchingControlName];

        if (!control || !matchingControl) {
          return;
        }

        // Only validate if both fields have values
        if (control.value && matchingControl.value) {
          if (control.value !== matchingControl.value) {
            matchingControl.setErrors({ mustMatch: true });
          } else if (matchingControl.hasError('mustMatch')) {
            delete matchingControl.errors.mustMatch;
            if (Object.keys(matchingControl.errors).length === 0) {
              matchingControl.setErrors(null);
            }
          }
        } else if (matchingControl.hasError('mustMatch')) {
          delete matchingControl.errors.mustMatch;
          if (Object.keys(matchingControl.errors).length === 0) {
            matchingControl.setErrors(null);
          }
        }
      };
    }

    // Validator to check if all password fields are filled or all are empty
    passwordFieldsValidator() {
      return (formGroup: FormGroup) => {
        const currentPass = formGroup.get('current_pass');
        const newPass = formGroup.get('password');
        const repeatPass = formGroup.get('repeat_pass');

        // Check if any password field has a value
        const anyPasswordFieldFilled = currentPass.value || newPass.value || repeatPass.value;
        
        if (anyPasswordFieldFilled) {
          // If any password field is filled, make all required
          [currentPass, newPass, repeatPass].forEach(control => {
            if (!control.value) {
              control.setErrors({ required: true });
            } else {
              if (control.hasError('required')) {
                delete control.errors.required;
                control.updateValueAndValidity();
              }
            }
          });
        } else {
          // If no password fields are filled, clear any required errors
          [currentPass, newPass, repeatPass].forEach(control => {
            if (control.hasError('required')) {
              delete control.errors.required;
              control.updateValueAndValidity();
            }
          });
        }
      };
    }
  
    getPasswordError(): string {
      const passwordControl = this.profileDetailsForm.get('current_pass');
      if (!passwordControl) return '';

      if (passwordControl.hasError('required')) {
        return 'Password is required';
      }
      
      if (passwordControl.hasError('minlength')) {
        return 'Password must be at least 8 characters long';
      }
      
      if (passwordControl.hasError('maxlength')) {
        return 'Password cannot exceed 20 characters';
      }
      
      if (passwordControl.hasError('invalidPattern')) {
        return 'Password is too week';
      }

      if (passwordControl.hasError('incorrectPassword')) {
        return 'Current Password is incorrect';
      }
      
      if (passwordControl.hasError('incorrectPassword')) {
        return 'Incorrect current password';
      }

      return this.errorObj.current_pass || '';
    }

    ngOnDestroy(): void {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    }
  }
  